import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/button/Button';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { paths } from '../../RouterContainer';
import { useTranslation } from '../../../context/translationContext';
import { columns } from '../../../components/columns/Company/UserTaskColumns';
import { TableFilter } from '../../../components/fIlter/TableFilter';
import { FilterItemType } from '../../../components/fIlter/TableFilter.types';
import { Loader } from '../../../components/loader/Loader';
import { UserTask, UserTaskFilter } from '../../../types/UserTask';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { format, startOfYear } from 'date-fns';
import { dateSubmitFormat } from '../../../types/core/date.types';
import useSessionStorage from '../../../hooks/useSessionStorage';
import { sessionStorageKeys } from '../../../utils/keys/sessionStorageKeys';
/**
 * Page component represents user tasks overview
 */
export const UserTaskPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<UserTask[] | null>(null);
  const { company } = useCurrentCompany();
  /**
   * Filter default values
   */
  const defaultFilterValues = {
    filterFrom: startOfYear(new Date()),
    filterTo: undefined,
  };
  const [filter, setFilter] = useSessionStorage<UserTaskFilter>(
    sessionStorageKeys.OVERVIEW_FILTER + 'userTasks',
    defaultFilterValues,
  );
  const [filteredTasks, setFilteredTasks] = useState<UserTask[] | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbs([{ path: paths.USER_TASKS, name: 'navigation.user-tasks' }]);
  }, []);

  /**
   * Loads user tasks
   */
  const fetchData = async () => {
    if (!company || !filter) return;
    let req = `?companyId=${company.id}`;
    if (filter.filterFrom)
      req += `&filterFrom=${format(new Date(filter.filterFrom), dateSubmitFormat)}`;
    if (filter.filterTo) req += `&filterTo=${format(new Date(filter.filterTo), dateSubmitFormat)}`;

    const response = await http.apiGet({
      url: `${apiUrl.USER_TASK_LIST_FILTERED}${req}`,
    });
    setTasks(response?.data);
    setFilteredTasks(response?.data);
  };

  useEffect(() => {
    fetchData();
  }, [filter, company]);

  /**
   * Redirects to company edit page
   * @param id of company
   */
  const onEditItem = (id: string | number) => {
    if (id === null) return;
    navigate(`/${paths.USER_TASKS}/edit/${id}`);
  };

  if (!tasks || !filteredTasks) return <Loader />;
  return (
    <>
      <div className="flex justify-between">
        <h1 className="text-3xl font-bold">{t('page.user-task.headline')}</h1>
        <Link to={`/${paths.USER_TASKS}/create`}>
          <Button
            title={t('general.create-new')}
            color="green"
            icon={<PlusIcon className="w-4 h-4 mr-2" />}
          />
        </Link>
      </div>
      <TableFilter
        filter={filter}
        setFilter={setFilter}
        filterOptions={[
          {
            type: FilterItemType.DATE,
            title: t('general.date-from'),
            valueKey: 'filterFrom',
          },
          {
            type: FilterItemType.DATE,
            title: t('general.date-to'),
            valueKey: 'filterTo',
          },
          {
            data: tasks,
            type: FilterItemType.FULLTEXT,
            setData: setFilteredTasks,
            includedFields: [
              'status',
              'description',
              'name',
              'vatCode',
              'responsibleUser',
              'fullName',
            ],
          },
        ]}
      />
      <DataTable data={filteredTasks} columns={columns} handleEdit={onEditItem} columnFilters />
    </>
  );
};
